import { contextWithCalculatedColumns } from "./calculatedColumns";
import type { ReportLogicContext } from "./context";
import type {
    ReportConfig,
    ModelKey,
    DataColumn,
    ExtraBreakdown,
    TripDataFieldDef,
    TripDataFieldState,
    FilterGroupKey,
    MetricGroup,
    MetricSection,
    DateGranularity,
    PlanConfig,
    DataModel,
    RuleSet,
    SectionExcelLayout,
    ReportSubTab,
} from "../types";
import {
    findColumnByIdentity,
    allColumnsForModel,
    columnsForModel,
    TRIP_DATA_FIELDS,
    tripDataColumnForKey,
    breakdownColumnForModel,
    targetFieldDef,
    extraBreakdownAlias,
    columnMatchesKey,
    resolveCountDistinctColumn,
    detailColumnsForModel,
    findSelectedDetailColumn,
    columnsMatch,
    dataColumnSelectionKey,
} from "./catalogResolution";
import { reportGlobalFilters, SLA_METRIC_DEFS } from "./metrics";
import {
    inClause,
    sqlLikeLiteral,
    dateSqlForColumn,
    sqlList,
    sqlLiteral,
    sqlAlias,
    buildLateConditionSql,
    otpRuleColSql,
    indentSql,
    countAllExpr,
    countMatchingExpr,
    safeSqlAlias,
    sqlCommentText,
    resultTextSqlForColumn,
} from "./sqlExpressions";
import { dateGranularityExpr } from "./reportingPeriods";
import {
    buildMetricFilterClauses,
    buildCatRulesColumns,
    buildRuleFilterClauses,
    virtualCategoryColumn,
    ruleResultSql,
    ruleSetModelKey,
    setRuleMatchExpression,
} from "./ruleInterpretation";
import type { ComputedColumn } from "./ruleInterpretation";
import { detailRuleSetOutputs, ruleSetCaseExpr } from "./detailRuleSets";
import { combinedCountOverrides, combinedRuleSetIds, usesCombinedRuleSets } from "./combinedRuleSetSelection";
import { combinedAxisColumnFor, combinedDistinctColumnFor, combinedOtherModelKeys, combinedRuleSetModelKey, combinedSectionForModel } from "./combinedRuleSets";
import { calculatedFieldSql, detailCalculatedColumns, findCalculatedField, isFormulaCalculatedField, measureAggregateSql, measureLabel, usesMeasureField } from "./calculatedFields";
import { detailConstAlias, detailConstantColumns, detailConstantIssue, detailConstantSql } from "./detailConstantColumns";
import { detailAggregateDef, detailAggregateOf, detailAggregateSql, detailOutputValueType, isDetailColumnOnSheet, usesDetailAggregates } from "./detailAggregates";
import { usesDetailRuleSetList } from "./detailRuleSetSelection";
import { isPeriodDateExtra, periodDateDimension, periodDateExpr, periodDateNarrative, periodDatePeriodClauses } from "./periodDate";
import { MODEL_PERIOD_OFF_CLAUSE, basedOnColumnFor, basedOnPeriodClause, metricOverridesReportPeriod, metricPeriodFilterClauses, modelAppliesReportingPeriod } from "./periodBounds";
import {
    buildWithRelatedJoins,
    hoistRelatedDimension,
    relatedCountAllExpr,
    relatedFromSql,
    relatedGroupApplyLines,
    relatedListNotIn,
    relatedValueSql,
    isRelatedColumn,
    rowsAreFanned,
} from "./relatedTables";

function reportExclusionClausesForModel(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey) {
    const clauses: string[] = [];
    const memberColumn = findColumnByIdentity(allColumnsForModel(context, modelKey), ["memberId", "member id"]);
    const invoiceColumn = findColumnByIdentity(allColumnsForModel(context, modelKey), ["invoiceId", "invoice id", "invoiceNumber", "invoice number", "invoiceNo", "invoice no"]);
    const filters = reportGlobalFilters(report);
    if (memberColumn && filters.exclusions.memberIds.length) clauses.push(relatedListNotIn(memberColumn, filters.exclusions.memberIds));
    if (invoiceColumn && filters.exclusions.invoiceNumbers.length) clauses.push(relatedListNotIn(invoiceColumn, filters.exclusions.invoiceNumbers));
    return clauses;
}

function tripDataFieldMatchExpr(context: ReportLogicContext, column: DataColumn, field: TripDataFieldDef, state: TripDataFieldState, modelKey: ModelKey, useIds = false) {
    const parts: string[] = [];
    const exactValues = state.values.map((value) => value.trim()).filter(Boolean);
    if (field.hasText && state.freeText.trim()) exactValues.push(state.freeText.trim());
    const uniqueExactValues = Array.from(new Set(exactValues));
    if (uniqueExactValues.length) parts.push(inClause(context, modelKey, column, uniqueExactValues, useIds));
    if (field.hasText && state.contains.trim()) parts.push(`${column.sql} LIKE ${sqlLikeLiteral(state.contains.trim(), "contains")}`);
    if (!parts.length) return null;
    const match = parts.length > 1 ? `(${parts.join(" OR ")})` : parts[0];
    return `(${column.sql} IS NOT NULL AND ${match})`;
}

function tripDataGroupExpr(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey, groupKey: FilterGroupKey, useIds = false) {
    if (modelKey !== "transportation") return null;
    const columns = columnsForModel(context, modelKey);
    const group = report.tripDataFilters[groupKey];
    const expressions = TRIP_DATA_FIELDS.map((field) => {
        const state = group.fields[field.key];
        const column = tripDataColumnForKey(context, field.column, columns);
        return state && column ? tripDataFieldMatchExpr(context, column, field, state, modelKey, useIds) : null;
    }).filter(Boolean) as string[];
    if (!expressions.length) return null;
    return expressions.length > 1 ? `(${expressions.join(group.logic === "or" ? " OR " : " AND ")})` : expressions[0];
}

function tripDataClausesForModel(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey, useIds = false) {
    const clauses: string[] = [];
    const includeExpr = tripDataGroupExpr(context, report, modelKey, "include", useIds);
    const excludeExpr = tripDataGroupExpr(context, report, modelKey, "exclude", useIds);
    if (includeExpr) clauses.push(`${includeExpr} -- Trip Data: only include rows matching this`);
    if (excludeExpr) clauses.push(`NOT (${excludeExpr}) -- Trip Data: leave out rows matching this`);
    return clauses;
}

// Start/end operators and the "if blank" fallback live in logic/periodBounds.ts.
// A data model whose reporting period is turned off in Setup gets no period clause at all.
function periodClauseForModel(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey) {
    if (!modelAppliesReportingPeriod(report, modelKey)) return MODEL_PERIOD_OFF_CLAUSE;
    return basedOnPeriodClause(context, report, modelKey);
}

function metricBreakdownExpr(groups: MetricGroup[], columnSql: string) {
    const activeGroups = groups.filter((group) => group.values.length);
    if (!activeGroups.length) return { select: columnSql, groupBy: columnSql };
    const sourceSql = columnSql.includes("\n") ? `(${columnSql})` : columnSql;
    const lines = ["CASE"];
    activeGroups.forEach((group) => lines.push(`  WHEN ${sourceSql} IN (${sqlList(group.values)}) THEN '${group.name.replace(/'/g, "''")}'`));
    lines.push(`  ELSE ${sourceSql}`);
    lines.push("END");
    const expression = lines.join("\n");
    return { select: expression, groupBy: expression };
}

function extraBreakdownExprs(context: ReportLogicContext, section: MetricSection, report?: ReportConfig) {
    if (!section.model) return [];
    const periodDateSql = report ? periodDateExpr(context, report, section) : null;
    return section.extraBreakdowns
        .map((extra) => {
            if (isPeriodDateExtra(extra)) {
                // A Period Date part has no single source column; `column` stays undefined.
                const dimension = periodDateDimension(periodDateSql, extra);
                return dimension ? { column: undefined, breakdown: { select: dimension.select, groupBy: dimension.groupBy }, granularity: extra.granularity } : null;
            }
            const column = breakdownColumnForModel(context, section.model, extra.column);
            if (!column) return null;
            const columnSql = relatedValueSql(column);
            const breakdown = extra.granularity ? dateGranularityExpr(dateSqlForColumn({ ...column, sql: columnSql }), extra.granularity) : metricBreakdownExpr(extra.groups, columnSql);
            return { column, breakdown, granularity: extra.granularity };
        })
        .filter(Boolean) as { column?: DataColumn; breakdown: { select: string; groupBy: string }; granularity?: DateGranularity }[];
}

// The client-scope predicate for one metric: the report plan's own client plus any
// Co-Plan clients selected on that metric (MetricSection.coPlanClientIds).
//
// This MIRRORS ReportSqlBuilder.ClientScopeClause on the backend. That builder produces
// the SQL that actually runs; this one produces the SQL shown in the preview tab, and a
// user who reads the preview is entitled to assume the report returns those rows - so
// the two derivations have to stay identical. Three details are load-bearing and match
// the backend deliberately:
//   - the plan's own client is unioned in HERE rather than stored on the section, so a
//     cloned report picks up its new plan's client (see the MetricSection note in
//     types.ts);
//   - a single id stays `ClientId = n` rather than a one-element IN list, so an
//     unchanged metric's SQL is byte-identical to what it was before Co-Plans existed;
//   - the plan-name fallback pre-dates Co-Plans and is kept: a plan with no numeric
//     clientId is matched by name against the model's client column. When such a plan
//     ALSO has Co-Plans the two are OR-ed rather than one replacing the other, since
//     dropping either side would silently lose one health plan's rows.
// The preview interpolates ids as literals where the backend binds SqlParameters; the
// ids are numbers filtered to positive integers here, and the backend re-validates them
// against active ClientInfos at save time.
function sectionClientScope(context: ReportLogicContext, plan: PlanConfig, modelKey: ModelKey, section?: MetricSection) {
    const model = context.dataModels[modelKey];
    if (!model) return null;
    // Metric-level switch: client filter off removes the client predicate completely - no
    // ClientId, no Co-Plan ids, no client-name match. The metric is scoped through its own
    // inclusion filters instead. Mirrors the early return in ReportSqlBuilder.ClientScopeClause.
    if (section && section.applyClientFilter === false) return null;

    const ids: number[] = [];
    const seen = new Set<number>();
    const addId = (value: unknown) => {
        const id = Number(value);
        if (!Number.isInteger(id) || id <= 0 || seen.has(id)) return;
        seen.add(id);
        ids.push(id);
    };

    addId(plan.clientId);
    const planHasClientId = ids.length > 0;
    (section?.coPlanClientIds || []).forEach(addId);

    const idClause = ids.length === 0
        ? null
        : ids.length === 1
            ? `ClientId = ${ids[0]}`
            : `ClientId IN (${ids.join(", ")})`;

    if (planHasClientId) return idClause;

    const nameClause = model.clientColumn ? `${model.clientColumn} = ${sqlLiteral(plan.clientName)}` : null;
    if (!idClause) return nameClause;
    return nameClause ? `(${nameClause} OR ${idClause})` : idClause;
}

// `section` is optional because this is also used for report-level SQL (the call centre
// SLA summary, a rule set's standalone preview, the per-model CTEs) where there is no one
// metric to take Co-Plans from - those stay scoped to the report plan's own client.
function buildBaseWhereClauses(context: ReportLogicContext, plan: PlanConfig, report: ReportConfig, modelKey: ModelKey, useIds = false, section?: MetricSection) {
    const model = context.dataModels[modelKey];
    if (!model) return [];
    // A metric whose Period Date also drives the reporting period is filtered by that derived
    // date instead of the report's anchor date (mirrors ReportSqlBuilder.BuildBaseWhereClauses).
    // A metric can also drop the report's reporting period and use its own filter instead.
    // The data model can turn the reporting period off for all its metrics (Setup); a metric's
    // own "custom" period filter still applies.
    const ownsSection = !!section && section.model === modelKey;
    const modelApplies = modelAppliesReportingPeriod(report, modelKey);
    const periodDateClauses = ownsSection && modelApplies ? periodDatePeriodClauses(context, report, section!, useIds) : null;
    const where = ownsSection && metricOverridesReportPeriod(section!)
        ? metricPeriodFilterClauses(context, report, section!, useIds)
        : periodDateClauses || [periodClauseForModel(context, report, modelKey)];
    const clientScope = sectionClientScope(context, plan, modelKey, section);
    if (clientScope) where.push(clientScope);
    reportExclusionClausesForModel(context, report, modelKey).forEach((clause) => where.push(clause));
    tripDataClausesForModel(context, report, modelKey, useIds).forEach((clause) => where.push(clause));
    return where;
}

function buildWhereClauses(context: ReportLogicContext, plan: PlanConfig, report: ReportConfig, section: MetricSection, column?: DataColumn, useIds = false) {
    if (!section.model) return [];
    const model = context.dataModels[section.model];
    if (!model) return [];
    return buildBaseWhereClauses(context, plan, report, section.model, useIds, section).concat(buildMetricFilterClauses(context, report, section, column, useIds));
}

function metricHeaderLines(context: ReportLogicContext, report: ReportConfig, model: DataModel, section: MetricSection, computedColumns: { fieldKey: string; alias: string; expr: string }[] = []) {
    const lines = [
        `-- Metric: ${section.name || "Untitled metric"}`,
        `-- Source: ${model.label} (${model.table})`,
    ];
    if (computedColumns.length) {
        lines.push(`-- Uses categorization: ${computedColumns.map((item) => targetFieldDef(context, item.fieldKey, report).label).join(", ")}`);
    }
    const periodDateText = periodDateNarrative(context, report, section);
    if (metricOverridesReportPeriod(section)) {
        lines.push("-- Reporting period: NOT applied to this metric; it uses its own filter (see WHERE).");
    } else if (!modelAppliesReportingPeriod(report, section.model)) {
        lines.push("-- Reporting period: NOT applied; turned off for this data model in Setup.");
    } else if (periodDateText) {
        lines.push(`-- Period Date: dated ${periodDateText.replace(/[\r\n]+/g, " ")}${section.periodDate?.applyToReportingPeriod ? " - also decides the reporting period" : ""}`);
    }
    return lines;
}

// Resolves the SELECT/GROUP BY expression + alias for one breakdown dimension (the primary
// breakdown column, or one extra breakdown). If the chosen rule set has a computed column that
// targets this exact underlying column, that CASE expression is used INSTEAD OF the raw column
// (so the query never selects/groups by both the raw column and its categorized version) - and the
// matched computed column is marked "consumed" so it isn't also added a second time as a leftover
// categorization column. Date-part granularity always wins when set, since a rule set targeting a
// timestamp column would be unusual and the user explicitly asked for a date-part breakdown there.
function resolveBreakdownDimension(context: ReportLogicContext,
    report: ReportConfig,
    column: DataColumn,
    groups: MetricGroup[],
    granularity: DateGranularity | undefined,
    ruleComputedColumns: ComputedColumn[],
    consumed: Set<ComputedColumn>,
) {
    if (granularity) {
        // dateSqlForColumn, not column.sql: a text-backed date column must be converted
        // first, exactly as ReportSqlBuilder.ResolveBreakdownDimension does - otherwise the
        // preview shows YEAR(<varchar>) while the query that runs converts it.
        const expr = dateGranularityExpr(dateSqlForColumn({ ...column, sql: relatedValueSql(column) }), granularity);
        return { select: expr.select, groupBy: expr.groupBy, alias: extraBreakdownAlias(column, granularity) };
    }
    const override = matchingComputedColumn(context, report, ruleComputedColumns, column);
    if (override) {
        consumed.add(override);
        return { select: override.expr, groupBy: override.expr, alias: sqlAlias(override.alias) };
    }
    const breakdown = metricBreakdownExpr(groups, relatedValueSql(column));
    return { select: breakdown.select, groupBy: breakdown.groupBy, alias: extraBreakdownAlias(column, undefined) };
}

// Every extra breakdown's SELECT/GROUP BY/alias, Period Date parts included. The Period Date
// expression is built ONCE per section and reused, so SELECT and GROUP BY stay textually
// identical (the backend relies on the same thing for its bound parameters).
function resolveExtraDimensions(context: ReportLogicContext,
    report: ReportConfig,
    section: MetricSection,
    periodDateSql: string | null,
    ruleComputedColumns: ComputedColumn[],
    consumed: Set<ComputedColumn>,
) {
    return section.extraBreakdowns
        .map((extra) => {
            if (isPeriodDateExtra(extra)) return periodDateDimension(periodDateSql, extra);
            const extraColumn = breakdownColumnForModel(context, section.model, extra.column);
            return extraColumn ? resolveBreakdownDimension(context, report, extraColumn, extra.groups, extra.granularity, ruleComputedColumns, consumed) : null;
        })
        .filter(Boolean) as { select: string; groupBy: string; alias: string }[];
}

// Columns stored in a related table (logic/relatedTables.ts): the query LEFT JOINs each related
// table whose columns it outputs (Full Row Detail: any number of them, a record repeating once per
// related row; every other metric: the one table its breakdowns use).
function buildSqlForSection(context: ReportLogicContext, plan: PlanConfig, report: ReportConfig, section: MetricSection, useIds = false) {
    // The report's own calculated fields are columns of its models (logic/calculatedColumns.ts).
    context = contextWithCalculatedColumns(context, report);
    const build = () => buildSqlForSectionCore(context, plan, report, section, useIds);
    const combined = usesCombinedRuleSets(section);
    const flatModel = !combined && section.model ? context.dataModels[section.model] : undefined;
    return buildWithRelatedJoins(section.name, build, section.displayAs === "detail" && !combined, flatModel);
}

function buildSqlForSectionCore(context: ReportLogicContext, plan: PlanConfig, report: ReportConfig, section: MetricSection, useIds = false) {
    if (!section.model || !section.breakdownColumn) return `-- "${section.name || "Untitled metric"}": pick a column to see its query.`;
    const model = context.dataModels[section.model];
    if (!model) return `-- "${section.name || "Untitled metric"}": selected data model is not available.`;
    const column = columnsForModel(context, section.model).find((item) => columnMatchesKey(item, section.breakdownColumn));
    if (!column) return `-- "${section.name || "Untitled metric"}": selected column is not available.`;

    // Combined categorizations have their own query shape; every other metric continues below.
    if (usesCombinedRuleSets(section)) return buildCombinedRuleSetsSql(context, plan, report, section, model, column, useIds);

    // Breakdown source is independent of displayAs: any metric (count, percentage, full detail, or
    // on-time-performance) can be bucketed by raw column values, and can ADDITIONALLY be categorized
    // by an explicitly chosen rule set. Categorization is never applied automatically - it only shows
    // up in the query when breakdownSource is "ruleSet" and a specific rule set has been picked. When
    // that happens, the rule set's computed columns/filters (buildCatRulesColumns/buildRuleFilterClauses)
    // are added ON TOP of whatever breakdown column and extra breakdowns are already configured, rather
    // than replacing them.
    const usesRuleSet = section.breakdownSource === "ruleSet";
    const ruleSet = usesRuleSet ? report.bucketRuleSets.find((set) => set.id === section.ruleSetId) : undefined;
    const where = buildWhereClauses(context, plan, report, section, column, useIds);
    const distinctColumn = resolveCountDistinctColumn(context, section.model, section.countMode, section.countDistinctColumn);
    const periodDateSql = periodDateExpr(context, report, section, useIds);

    if (section.displayAs === "detail") {
        const detailColumns = detailColumnsForModel(context, section.model);
        const selected = section.selectedColumns.length
            ? section.selectedColumns.map((key) => findSelectedDetailColumn(detailColumns, key)).filter(Boolean) as DataColumn[]
            : [];
        // Multiple rule sets (detailRuleSetIds) replace the single breakdownSource/ruleSetId pair
        // for a detail metric; a config saved before the list existed keeps the legacy path
        // below unchanged. See logic/detailRuleSets.ts for the per-rule-set semantics.
        const usesRuleSetList = usesDetailRuleSetList(section);
        const legacyRuleSet = !usesRuleSetList && usesRuleSet;
        const detailOutputs = usesRuleSetList ? detailRuleSetOutputs(context, report, section, useIds, column) : [];
        // List mode never recodes a data column in place: each rule set gets its own column below.
        const computedColumns = usesRuleSetList
            ? []
            : legacyRuleSet ? buildCatRulesColumns(context, report, ruleSet, useIds, column) : [];
        const ruleFilterClauses = usesRuleSetList
            ? detailOutputs.flatMap((output) => output.filterClauses)
            : legacyRuleSet ? buildRuleFilterClauses(context, report, ruleSet, useIds) : [];
        const finalWhere = where.concat(ruleFilterClauses);
        const lines = metricHeaderLines(context, report, model, section, computedColumns);
        if (legacyRuleSet && !ruleSet) lines.push("-- Pick a rule set for this metric to finish this query.");
        if (detailOutputs.length) {
            lines.push(`-- Categorization columns (each rule set evaluated independently): ${detailOutputs
                .map((output) => sqlCommentText(output.ruleSet && output.label !== output.ruleSet.name.trim() ? `${output.label} = ${output.ruleSet.name}` : output.label))
                .join(", ")}`);
        }
        // When a "detail" Excel layout is configured, order the SELECT list to match the
        // sheet's column order and alias every column explicitly so the SQL preview lines
        // up with the workbook layout. Without one, behavior is unchanged from before.
        const detailLayout = section.excelLayout?.mode === "detail" ? section.excelLayout : undefined;
        // The user's alias (else the rule set's name) is the column name in the query.
        const virtualComputedColumns = (usesRuleSetList
            ? detailOutputs
                .filter((output) => output.columnExpr)
                .map((output) => `${output.columnExpr} AS ${sqlAlias(output.label)}`)
            : computedColumns
                .filter((item) => targetFieldDef(context, item.fieldKey, report).virtual)
                .map((item) => `${item.expr} AS ${sqlAlias(item.alias)}`))
            // Calculated fields, after the categorization columns - same order as the server.
            .concat(detailCalculatedColumns(context, report, section)
                .filter((item) => item.expr)
                .map((item) => `${item.expr} AS ${sqlAlias(item.label)}`))
            // Fixed value columns last - same order as the server. The server binds each value
            // as a typed parameter; the preview shows it as a literal.
            .concat(detailConstantColumns(section)
                .filter((column) => !detailConstantIssue(section, column) && detailConstantSql(column))
                .map((column) => `${detailConstantSql(column)} AS ${sqlAlias((column.name || "").trim())}`));
        const incompleteCalcColumns = detailCalculatedColumns(context, report, section).filter((item) => !item.expr);
        if (incompleteCalcColumns.length) {
            lines.push(`-- Not shown until finished on the Calculated Fields tab: ${sqlCommentText(incompleteCalcColumns.map((item) => item.label).join(", "))}`);
        }
        const orderedSelected = detailLayout ? orderDetailColumnsByLayout(selected, detailLayout, detailColumns) : selected;
        // Aggregated sheet columns (Excel Layout tab) turn the dump into a grouped query
        // (logic/detailAggregates.ts) - same outputs, same order as the server builds them.
        if (detailLayout && usesDetailAggregates(section)) {
            const outputs: AggregatedDetailOutput[] = [
                ...(orderedSelected.length ? orderedSelected : detailColumns).map((column) => ({
                    sourceAlias: `detail:${dataColumnSelectionKey(column)}`,
                    label: column.label,
                    expr: sourceSqlForColumn(context, report, computedColumns, column),
                    column,
                })),
                ...(usesRuleSetList
                    ? detailOutputs.filter((output) => output.columnExpr).map((output) => ({ sourceAlias: output.alias, label: output.label, expr: output.columnExpr! }))
                    : computedColumns
                        .filter((item) => targetFieldDef(context, item.fieldKey, report).virtual)
                        .map((item) => ({ sourceAlias: item.alias, label: item.alias, expr: item.expr }))),
                ...detailCalculatedColumns(context, report, section)
                    .filter((item) => item.expr)
                    .map((item) => ({ sourceAlias: item.alias, label: item.label, expr: item.expr! })),
                ...detailConstantColumns(section)
                    .filter((column) => !detailConstantIssue(section, column) && detailConstantSql(column))
                    .map((column) => ({ sourceAlias: detailConstAlias(column.id), label: (column.name || "").trim(), expr: detailConstantSql(column)! })),
            ];
            return buildAggregatedDetailSql(context, report, section, model, detailLayout, outputs, finalWhere, lines);
        }
        const selectItems = orderedSelected.length
            ? orderedSelected.map((item) => detailLayout
                ? detailSelectSqlWithLayoutAlias(context, report, computedColumns, item, detailLayout)
                : detailSelectSqlForColumn(context, report, computedColumns, item)).concat(virtualComputedColumns)
            : ["*", ...virtualComputedColumns];
        if (!selectItems.length || (!orderedSelected.length && !detailColumns.length && !virtualComputedColumns.length)) {
            lines.push("-- No visible DataModelColumn records are configured for this data model.");
            return lines.join("\n");
        }
        // "Unique rows only": DISTINCT over everything selected, categorization columns included.
        lines.push(`SELECT${section.detailDistinct ? " DISTINCT" : ""}\n  ${selectItems.join(",\n  ")}`);
        // One row per record: a column stored in a related table (JoinKeyColumn) is shown as
        // the record's value list, never joined (logic/relatedTables.ts).
        lines.push(`FROM ${relatedFromSql(model)}`);
        lines.push(`WHERE ${finalWhere.join("\n  AND ")};`);
        return lines.join("\n");
    }

    if (section.displayAs === "otp") {
        const ruleComputedColumns = usesRuleSet ? buildCatRulesColumns(context, report, ruleSet, useIds, column) : [];
        const ruleFilterClauses = usesRuleSet ? buildRuleFilterClauses(context, report, ruleSet, useIds) : [];
        const finalWhere = where.concat(ruleFilterClauses);
        const consumedRuleColumns = new Set<ComputedColumn>();
        const breakdown = resolveBreakdownDimension(context, report, column, section.groups, undefined, ruleComputedColumns, consumedRuleColumns);
        const extras = resolveExtraDimensions(context, report, section, periodDateSql, ruleComputedColumns, consumedRuleColumns);
        const leftoverRuleColumns = ruleComputedColumns.filter((item) => !consumedRuleColumns.has(item));
        const lateCondition = buildLateConditionSql(context, section, useIds);
        const missingParts = new Set<string>();
        section.otpRules.forEach((rule) => {
            const compare = otpRuleColSql(context, section, rule.compareCol);
            const against = otpRuleColSql(context, section, rule.againstCol);
            if (compare) missingParts.add(`${compare} IS NULL`);
            if (against) missingParts.add(`${against} IS NULL`);
        });
        const missingCondition = Array.from(missingParts).join(" OR ");
        const onTimeCase = missingCondition
            ? `CASE WHEN (${missingCondition}) THEN ${section.otpMissingHandling === "late" ? "0" : section.otpMissingHandling === "ontime" ? "1" : "NULL"} WHEN (${lateCondition || "1=0"}) THEN 0 ELSE 1 END`
            : `CASE WHEN (${lateCondition || "1=0"}) THEN 0 ELSE 1 END`;
        const lines = metricHeaderLines(context, report, model, section, ruleComputedColumns);
        if (usesRuleSet && !ruleSet) lines.push("-- Pick a rule set for this metric to finish this query.");
        if (!lateCondition) lines.push("-- Add at least one late-arrival rule above to complete this query.");
        if (section.countMode === "distinct" && !distinctColumn) lines.push("-- Distinct counting is on, but no column is chosen yet - using COUNT(*) until one is picked.");
        lines.push("SELECT");
        const groupBy: string[] = [];
        if (!section.noBreakdown) {
            const virtualCategory = virtualCategoryColumn(ruleComputedColumns);
            const primaryBreakdown = virtualCategory
                ? { select: virtualCategory.expr, groupBy: virtualCategory.expr, alias: sqlAlias(virtualCategory.alias) }
                : breakdown;
            if (virtualCategory) consumedRuleColumns.add(virtualCategory);
            lines.push(`  ${primaryBreakdown.select} AS ${primaryBreakdown.alias},`);
            groupBy.push(primaryBreakdown.groupBy);
            extras.forEach((item) => {
                lines.push(`  ${item.select} AS ${item.alias},`);
                groupBy.push(item.groupBy);
            });
            leftoverRuleColumns.forEach((item) => {
                lines.push(`  ${indentSql(item.expr, 2)} AS ${sqlAlias(item.alias)},`);
                groupBy.push(item.expr);
            });
        }
        const otpNumerator = distinctColumn
            ? `COUNT(DISTINCT CASE WHEN (${onTimeCase}) = 1 THEN ${distinctColumn.sql} END)`
            : `SUM(${onTimeCase})`;
        const otpDenominator = section.otpMissingHandling === "exclude" && missingCondition
            ? (distinctColumn
                ? `NULLIF(COUNT(DISTINCT CASE WHEN NOT (${missingCondition}) THEN ${distinctColumn.sql} END),0)`
                : `NULLIF(COUNT(CASE WHEN NOT (${missingCondition}) THEN 1 END),0)`)
            : (distinctColumn
                ? `NULLIF(COUNT(DISTINCT ${distinctColumn.sql}),0)`
                : `NULLIF(COUNT(*),0)`);
        lines.push(`  ${otpNumerator} * 100.0 / ${otpDenominator} AS OnTimePercentage`);
        lines.push(`FROM ${relatedFromSql(model)}`);
        lines.push(`WHERE ${finalWhere.join("\n  AND ")}`);
        if (groupBy.length) lines.push(`GROUP BY ${groupBy.join(",\n  ")};`);
        else lines[lines.length - 1] += ";";
        return lines.join("\n");
    }

    // count / percentage
    const ruleComputedColumns = usesRuleSet ? buildCatRulesColumns(context, report, ruleSet, useIds, column) : [];
    const ruleFilterClauses = usesRuleSet ? buildRuleFilterClauses(context, report, ruleSet, useIds) : [];
    const finalWhere = where.concat(ruleFilterClauses);
    const lines = metricHeaderLines(context, report, model, section, ruleComputedColumns);
    if (usesRuleSet && !ruleSet) lines.push("-- Pick a rule set for this metric to finish this query.");
    else if (usesRuleSet && !ruleComputedColumns.length && !ruleFilterClauses.length) lines.push("-- The chosen rule set has no complete rules yet.");
    // A calculated-field measure (Sum / Average / Min / Max) replaces the row count.
    const measureSql = measureAggregateSql(context, report, section);
    if (usesMeasureField(section) && !measureSql) lines.push("-- The measure's calculated field is not complete yet - counting rows until it is.");
    else if (!measureSql && section.countMode === "distinct" && !distinctColumn) lines.push("-- Distinct counting is on, but no column is chosen yet - using COUNT(*) until one is picked.");

    const consumedRuleColumns = new Set<ComputedColumn>();
    const virtualCategory = virtualCategoryColumn(ruleComputedColumns);
    const breakdown = virtualCategory
        ? { select: virtualCategory.expr, groupBy: virtualCategory.expr, alias: sqlAlias(virtualCategory.alias) }
        : resolveBreakdownDimension(context, report, column, section.groups, undefined, ruleComputedColumns, consumedRuleColumns);
    if (virtualCategory) consumedRuleColumns.add(virtualCategory);
    // A dimension testing a related table (EXISTS) is grouped through CROSS APPLY.
    const groupApply: string[] = [];
    const extras = resolveExtraDimensions(context, report, section, periodDateSql, ruleComputedColumns, consumedRuleColumns)
        .map((item) => hoistRelatedDimension(item, groupApply));
    const leftoverRuleColumns = ruleComputedColumns.filter((item) => !consumedRuleColumns.has(item));
    const hoistedBreakdown = hoistRelatedDimension(breakdown, groupApply);
    const groupBy = [hoistedBreakdown.groupBy];
    lines.push("SELECT", `  ${hoistedBreakdown.select} AS ${hoistedBreakdown.alias},`);
    extras.forEach((item) => {
        lines.push(`  ${item.select} AS ${item.alias},`);
        groupBy.push(item.groupBy);
    });
    leftoverRuleColumns.forEach((item) => {
        lines.push(`  ${indentSql(item.expr, 2)} AS ${sqlAlias(item.alias)},`);
        groupBy.push(item.expr);
    });
    const countExpr = measureSql || relatedCountAllExpr(model, distinctColumn);
    if (measureSql && rowsAreFanned(model) && /^(SUM|AVG)\(/.test(measureSql)) {
        lines.push("-- Not runnable: a breakdown uses a related table, so each record can appear in several rows and a sum or average would count it more than once.");
    }
    const percentagePartitionBy = extras.map((item) => item.groupBy).concat(leftoverRuleColumns.map((item) => item.expr));
    const percentageOver = percentagePartitionBy.length ? `OVER (PARTITION BY ${percentagePartitionBy.join(", ")})` : "OVER ()";
    if (measureSql) {
        // A sum can total zero (unlike a row count), so the denominator is NULLIF-guarded.
        lines.push(section.displayAs === "percentage"
            ? `  ${measureSql} * 100.0 / NULLIF(SUM(${measureSql}) ${percentageOver}, 0) AS Percentage`
            : `  ${measureSql} AS ${sqlAlias(safeSqlAlias(measureLabel(report, section)))}`);
    } else {
        lines.push(section.displayAs === "percentage" ? `  ${countExpr} * 100.0 / SUM(${countExpr}) ${percentageOver} AS Percentage` : `  ${countExpr} AS TotalCount`);
    }
    lines.push(`FROM ${relatedFromSql(model)}`);
    lines.push(...relatedGroupApplyLines(groupApply));
    lines.push(`WHERE ${finalWhere.join("\n  AND ")}`);
    lines.push(`GROUP BY ${groupBy.join(",\n  ")};`);
    return lines.join("\n");
}

// Combined categorizations (breakdownSource "ruleSets"): several rule sets, each evaluated on
// its own, stacked into one result with ONE scan of the table. Each rule set is one row of a
// CROSS APPLY (VALUES ...) list, and its own include/exclude rules are folded into its CASE so
// they only decide whether THAT rule set counts the row. Mirrors
// ReportSqlBuilder.BuildCombinedRuleSetSqlAsync - the two must stay in step. The preview shows
// values as literals where the server binds parameters.
const COMBINED_APPLY = "rc_rs";

// Keep the preview's extra count measures aligned with the server's UNION result shape.
function appendCombinedCountOverrides(context: ReportLogicContext, report: ReportConfig, section: MetricSection, modelKey: ModelKey, lines: string[]) {
    combinedCountOverrides(section).forEach((row, index) => {
        const ruleSet = report.bucketRuleSets.find((set) => set.id === row.ruleSetId);
        const ownerKey = ruleSet ? combinedRuleSetModelKey(context, report, section, ruleSet) : "";
        const column = resolveCountDistinctColumn(context, ownerKey, row.countMode || "all", row.countDistinctColumn || "");
        const expression = ownerKey === modelKey ? relatedCountAllExpr(context.dataModels[modelKey], column) : "CAST(0 AS int)";
        lines[lines.length - 1] += ",";
        lines.push(`  ${expression} AS ${sqlAlias(`measure:count:${index}`)}`);
    });
}

function buildCombinedRuleSetsSql(context: ReportLogicContext, plan: PlanConfig, report: ReportConfig, section: MetricSection, model: DataModel, column: DataColumn, useIds = false) {
    const lines = metricHeaderLines(context, report, model, section);
    const branches = combinedRuleSetIds(section).map((id) => {
        const ruleSet = report.bucketRuleSets.find((set) => set.id === id);
        const caseExpr = ruleSet ? ruleSetCaseExpr(context, report, ruleSet, useIds, column) : null;
        return { id, ruleSet, caseExpr, filters: ruleSet ? buildRuleFilterClauses(context, report, ruleSet, useIds) : [] };
    });
    branches.filter((branch) => !branch.ruleSet).forEach(() => lines.push("-- A combined rule set no longer exists - remove it from this metric."));
    branches.filter((branch) => branch.ruleSet && !branch.caseExpr).forEach((branch) => lines.push(`-- Rule set "${sqlCommentText(branch.ruleSet!.name)}" has no complete Set rule, so it adds no categories.`));
    const usable = branches.filter((branch) => branch.ruleSet && branch.caseExpr);
    if (!usable.length) {
        lines.push("-- Add the rule sets to combine to see this metric's query.");
        return lines.join("\n");
    }
    lines.push(`-- Combined categorizations, each evaluated on its own: ${sqlCommentText(usable.map((branch) => branch.ruleSet!.name || branch.id).join(", "))}`);

    // Rule sets on other data models: one SELECT per model (mirrors ReportSqlBuilder's
    // BuildMultiModelCombinedRuleSetSql). A metric whose rule sets are all on its own model
    // continues below exactly as before.
    if (combinedOtherModelKeys(context, report, section).length) {
        return buildMultiModelCombinedSql(context, plan, report, section, column, useIds, lines, usable.map((branch) => ({ id: branch.id, ruleSet: branch.ruleSet!, filters: branch.filters })));
    }

    const measureSql = measureAggregateSql(context, report, section);
    if (usesMeasureField(section) && !measureSql) lines.push("-- The measure's calculated field is not complete yet - counting rows until it is.");
    const distinctColumn = resolveCountDistinctColumn(context, section.model, section.countMode, section.countDistinctColumn);
    if (!measureSql && section.countMode === "distinct" && !distinctColumn) lines.push("-- Distinct counting is on, but no column is chosen yet - using COUNT(*) until one is picked.");
    const countExpr = measureSql || relatedCountAllExpr(model, distinctColumn);

    const periodDateSql = periodDateExpr(context, report, section, useIds);
    const groupApply: string[] = [];
    const extras = resolveExtraDimensions(context, report, section, periodDateSql, [], new Set<ComputedColumn>())
        .map((item) => hoistRelatedDimension(item, groupApply));
    // Metric include/exclude VALUE lists belong to the breakdown column, which is only the
    // ELSE fallback here, so (like the server) they are not applied; filter conditions are.
    const where = buildBaseWhereClauses(context, plan, report, section.model, useIds, section)
        .concat(buildMetricFilterClauses(context, report, section, undefined, useIds))
        .concat([`${COMBINED_APPLY}.RcCategory IS NOT NULL -- rows a rule set does not categorize are left out of that rule set only`]);

    const groupBy = [`${COMBINED_APPLY}.RcRuleSetKey`, `${COMBINED_APPLY}.RcCategory`];
    lines.push("SELECT", `  ${COMBINED_APPLY}.RcRuleSetKey AS RuleSet,`, `  ${COMBINED_APPLY}.RcCategory AS Category,`);
    extras.forEach((item) => {
        lines.push(`  ${item.select} AS ${item.alias},`);
        groupBy.push(item.groupBy);
    });
    lines.push(`  ${countExpr} AS TotalCount`);
    appendCombinedCountOverrides(context, report, section, section.model, lines);
    lines.push(`FROM ${relatedFromSql(model)}`);
    lines.push(...relatedGroupApplyLines(groupApply));
    lines.push("CROSS APPLY (VALUES");
    usable.forEach((branch, index) => {
        lines.push(`  -- ${index + 1}. ${sqlCommentText(branch.ruleSet!.name || branch.id)}`);
        const category = branch.filters.length
            ? `CASE WHEN\n${branch.filters.map((clause, clauseIndex) => `      ${clauseIndex ? "AND " : ""}${clause}`).join("\n")}\n    THEN\n${indentSql(branch.caseExpr!, 6)}\n    END`
            : `\n${indentSql(branch.caseExpr!, 4)}`;
        lines.push(`  (${sqlLiteral(branch.id)}, ${category}\n  )${index < usable.length - 1 ? "," : ""}`);
    });
    lines.push(`) AS ${COMBINED_APPLY} (RcRuleSetKey, RcCategory)`);
    lines.push(`WHERE ${where.join("\n  AND ")}`);
    lines.push(`GROUP BY ${groupBy.join(",\n  ")};`);
    return lines.join("\n");
}

function combinedBranchSql(id: string, name: string, caseExpr: string, filters: string[], number: number, last: boolean) {
    const category = filters.length
        ? `CASE WHEN\n${filters.map((clause, clauseIndex) => `      ${clauseIndex ? "AND " : ""}${clause}`).join("\n")}\n    THEN\n${indentSql(caseExpr, 6)}\n    END`
        : `\n${indentSql(caseExpr, 4)}`;
    return [`  -- ${number}. ${sqlCommentText(name || id)}`, `  (${sqlLiteral(id)}, ${category}\n  )${last ? "" : ","}`];
}

function buildMultiModelCombinedSql(
    context: ReportLogicContext,
    plan: PlanConfig,
    report: ReportConfig,
    section: MetricSection,
    column: DataColumn,
    useIds: boolean,
    lines: string[],
    usable: { id: string; ruleSet: RuleSet; filters: string[] }[],
) {
    lines.push("-- Rule sets use different data models: one SELECT per data model, each with its own reporting period date, joined with UNION ALL.");
    // Metric's own model first, then the others in selection order.
    const modelKeys: ModelKey[] = [];
    const byModel = new Map<ModelKey, typeof usable>();
    usable.forEach((branch) => {
        const modelKey = combinedRuleSetModelKey(context, report, section, branch.ruleSet) || section.model;
        if (!modelKey) return;
        if (!byModel.has(modelKey)) { byModel.set(modelKey, []); modelKeys.push(modelKey); }
        byModel.get(modelKey)!.push(branch);
    });
    modelKeys.sort((a, b) => (a === section.model ? 0 : 1) - (b === section.model ? 0 : 1));

    // Column names come from the first SELECT of a UNION; the metric model's are used for all.
    const metricAliases = resolveExtraDimensions(context, report, section, periodDateExpr(context, report, section, useIds), [], new Set<ComputedColumn>()).map((item) => item.alias);
    let number = 0;
    modelKeys.forEach((modelKey, groupIndex) => {
        const groupModel = context.dataModels[modelKey];
        if (!groupModel) return;
        const isMetricModel = modelKey === section.model;
        // This model's view of the metric: Period Date / own period filter only where its columns exist.
        const { section: modelSection, notes } = combinedSectionForModel(context, report, section, modelKey);
        const periodDateSql = periodDateExpr(context, report, modelSection, useIds);
        const basedOn = basedOnColumnFor(context, report, modelKey);
        // The metric's breakdowns, in order. On another model each uses that model's matching
        // column (a Period Date part: its Period Date, else its Based On date); one with no match
        // is blank for this model's rows, so every SELECT has the same columns.
        const dimension = (extra: ExtraBreakdown) =>
            resolveExtraDimensions(context, report, { ...modelSection, extraBreakdowns: [extra] }, periodDateSql, [], new Set<ComputedColumn>())[0] || null;
        const extras = isMetricModel
            ? resolveExtraDimensions(context, report, section, periodDateSql, [], new Set<ComputedColumn>()).map((item) => ({ item, note: "" }))
            : section.extraBreakdowns.flatMap((extra) => {
                if (isPeriodDateExtra(extra)) {
                    const mapped = periodDateSql ? extra : basedOn ? { ...extra, source: "column" as const, column: basedOn.key } : null;
                    const item = mapped ? dimension(mapped) : null;
                    return item ? [{ item, note: "" }] : [];
                }
                const metricColumn = breakdownColumnForModel(context, section.model, extra.column);
                if (!metricColumn) return [];
                const axis = combinedAxisColumnFor(context, report, extra, metricColumn, modelKey);
                const item = axis ? dimension({ ...extra, column: axis.key }) : null;
                return [item
                    ? { item, note: "" }
                    : { item: null, note: `${groupModel.label} has no ${metricColumn.label}: that breakdown is blank for its rows` }];
            });
        const fallback = isMetricModel ? column : breakdownColumnForModel(context, modelKey, section.breakdownColumn);
        const where = buildBaseWhereClauses(context, plan, report, modelKey, useIds, modelSection)
            .concat(buildMetricFilterClauses(context, report, modelSection, undefined, useIds))
            .concat([`${COMBINED_APPLY}.RcCategory IS NOT NULL -- rows a rule set does not categorize are left out of that rule set only`]);
        const groupBy = [`${COMBINED_APPLY}.RcRuleSetKey`, `${COMBINED_APPLY}.RcCategory`];
        const groupApply: string[] = [];

        if (groupIndex > 0) lines.push("UNION ALL");
        lines.push(`-- Data model: ${sqlCommentText(groupModel.label)} (${groupModel.table})`);
        notes.forEach((note) => lines.push(`-- ${sqlCommentText(note)}`));
        lines.push("SELECT", `  ${COMBINED_APPLY}.RcRuleSetKey AS RuleSet,`, `  ${COMBINED_APPLY}.RcCategory AS Category,`);
        extras.forEach(({ item, note }, index) => {
            if (note) lines.push(`  -- ${sqlCommentText(note)}`);
            if (!item) {
                lines.push(`  CAST(NULL AS NVARCHAR(4000)) AS ${metricAliases[index]},`);
                return;
            }
            const hoisted = hoistRelatedDimension(item, groupApply);
            lines.push(`  ${hoisted.select} AS ${metricAliases[index] || hoisted.alias},`);
            groupBy.push(hoisted.groupBy);
        });
        const measure = combinedMeasureFor(context, report, section, modelKey);
        const distinct = combinedDistinctColumnFor(context, section, modelKey);
        const countNote = measure.note || (measure.sql ? null : distinct.note);
        if (countNote) lines.push(`  -- ${sqlCommentText(countNote)}`);
        lines.push(`  ${measure.sql || relatedCountAllExpr(groupModel, distinct.column)} AS TotalCount`);
        appendCombinedCountOverrides(context, report, section, modelKey, lines);
        lines.push(`FROM ${relatedFromSql(groupModel)}`);
        lines.push(...relatedGroupApplyLines(groupApply));
        lines.push("CROSS APPLY (VALUES");
        const branches = byModel.get(modelKey)!;
        branches.forEach((branch, index) => {
            number++;
            const caseExpr = ruleSetCaseExpr(context, report, branch.ruleSet, useIds, fallback) || "NULL";
            lines.push(...combinedBranchSql(branch.id, branch.ruleSet.name, caseExpr, branch.filters, number, index === branches.length - 1));
        });
        lines.push(`) AS ${COMBINED_APPLY} (RcRuleSetKey, RcCategory)`);
        lines.push(`WHERE ${where.join("\n  AND ")}`);
        lines.push(`GROUP BY ${groupBy.join(",\n  ")}`);
    });
    lines[lines.length - 1] += ";";
    return lines.join("\n");
}

/**
 * A calculated-field measure on one data model of a combined metric. Mirrors CombinedMeasureFor:
 * the metric's model uses it as usual; another model calculates the same field on its own
 * columns when it has all of them, else counts its rows. Null SQL = count rows.
 */
function combinedMeasureFor(context: ReportLogicContext, report: ReportConfig, section: MetricSection, modelKey: ModelKey): { sql: string | null; note: string | null } {
    if (!usesMeasureField(section)) return { sql: null, note: null };
    if (modelKey === section.model) return { sql: measureAggregateSql(context, report, section), note: null };
    const field = findCalculatedField(report, section.measure!.fieldId);
    if (!field || !isFormulaCalculatedField(field)) return { sql: null, note: null };
    const name = (field.name || "").trim() || field.id;
    const label = context.dataModels[modelKey]?.label || modelKey;
    const sql = calculatedFieldSql(context, report, { ...field, model: modelKey });
    return sql
        ? { sql: `SUM(${sql})`, note: `${name} calculated on ${label} columns` }
        : { sql: null, note: `${label} lacks a column ${name} uses: counting its rows instead` };
}

// Kept as the name the rest of the preview uses; the resolution itself lives in
// ruleInterpretation so the Set-rule value source can share it.
function modelKeyForRuleSet(context: ReportLogicContext, report: ReportConfig, ruleSet: RuleSet) {
    return ruleSetModelKey(context, report, ruleSet);
}

function buildCategorizationRuleSetSql(context: ReportLogicContext, plan: PlanConfig, report: ReportConfig, ruleSet: RuleSet) {
    context = contextWithCalculatedColumns(context, report);
    const modelKey = modelKeyForRuleSet(context, report, ruleSet);
    const model = modelKey ? context.dataModels[modelKey] : null;
    if (!model) return `-- "${ruleSet.name}": no Data Model is available for this rule set.`;

    const computedColumns = buildCatRulesColumns(context, report, ruleSet);
    const ruleFilterClauses = buildRuleFilterClauses(context, report, ruleSet);
    const baseSection = {
        model: modelKey,
        include: [],
        exclude: [],
        filterColumn: "",
        filterValues: [],
        excludeFilterColumn: "",
        excludeFilterValues: [],
    } as unknown as MetricSection;
    const where = buildWhereClauses(context, plan, report, baseSection).concat(ruleFilterClauses);
    const lines = [`-- Categorization rule set: "${ruleSet.name}" - ${model.label} (${model.table})`];

    if (!computedColumns.length && !ruleFilterClauses.length) {
        lines.push("-- Add at least one completed Set/Include/Exclude rule to generate SQL.");
        return lines.join("\n");
    }

    if (computedColumns.length) {
        lines.push("SELECT");
        computedColumns.forEach((item) => lines.push(`${indentSql(item.expr, 2)} AS ${sqlAlias(item.alias)},`));
        lines.push("  COUNT(*) AS RecordCount");
        lines.push(`FROM ${model.table}`);
        lines.push(`WHERE ${where.join("\n  AND ")}`);
        lines.push(`GROUP BY ${computedColumns.map((item) => item.expr).join(",\n  ")};`);
        return lines.join("\n");
    }

    lines.push("SELECT");
    lines.push("  COUNT(*) AS RecordCount");
    lines.push(`FROM ${model.table}`);
    lines.push(`WHERE ${where.join("\n  AND ")};`);
    return lines.join("\n");
}

function buildCategorizationSqlPreview(context: ReportLogicContext, plan: PlanConfig, report: ReportConfig) {
    if (!report.models.length) return "-- Add a Data Model before creating Categorization Rule SQL.";
    if (!report.bucketRuleSets.length) return "-- No Categorization Rule Sets defined yet.";
    return report.bucketRuleSets.map((ruleSet) => buildCategorizationRuleSetSql(context, plan, report, ruleSet)).join("\n\n");
}

function buildCallCenterSql(context: ReportLogicContext, plan: PlanConfig, report: ReportConfig, useIds = false) {
    const modelKey: ModelKey = "callcenter";
    const model = context.dataModels[modelKey];
    if (!model || !report.models.includes(modelKey)) return "-- Call center targets are enabled, but the Call Center Data Model is not attached to this report.";
    const enabledTargets = SLA_METRIC_DEFS.filter((definition) => report.callCenterSla.slaMetrics[definition.key]?.enabled);
    if (!enabledTargets.length) return "-- Call center targets are enabled, but no SLA metric is selected.";

    const where = buildBaseWhereClauses(context, plan, report, modelKey, useIds);
    const distinctColumn = resolveCountDistinctColumn(context, modelKey, report.callCenterSla.countMode, report.callCenterSla.countDistinctColumn);
    const denominator = `NULLIF(${countAllExpr(distinctColumn)},0)`;
    const header = [`-- Call center SLA summary - ${model.label} (${model.table})`];
    if (report.callCenterSla.countMode === "distinct" && !distinctColumn) header.push("-- Distinct counting is on, but no column is chosen yet - using COUNT(*) until one is picked.");
    const selectLines = enabledTargets.map((definition) => {
        const metric = report.callCenterSla.slaMetrics[definition.key];
        switch (definition.key) {
            case "answer":
            case "secondary":
                return `  ${countMatchingExpr(distinctColumn, `TalkTimeSeconds > 0 AND HoldTimeSeconds <= ${metric.seconds || 0}`)} * 100.0 / ${denominator} AS ${sqlAlias(safeSqlAlias(definition.label))}`;
            case "abandoned":
                return `  ${countMatchingExpr(distinctColumn, "IsAbandoned = 1")} * 100.0 / ${denominator} AS ${sqlAlias(safeSqlAlias(definition.label))}`;
            case "hold":
                return `  ${countMatchingExpr(distinctColumn, `HoldTimeSeconds <= ${metric.seconds || 0}`)} * 100.0 / ${denominator} AS ${sqlAlias(safeSqlAlias(definition.label))}`;
            default:
                return null;
        }
    }).filter(Boolean) as string[];

    return [
        ...header,
        "SELECT",
        selectLines.join(",\n"),
        `FROM ${model.table}`,
        `WHERE ${where.join("\n  AND ")};`,
    ].join("\n");
}

function cteNameForModel(modelKey: ModelKey) {
    return safeSqlAlias(`Base_${modelKey || "Data"}`);
}

function reportModelKeysForSql(report: ReportConfig) {
    const keys = new Set<ModelKey>();
    report.sections.forEach((section) => {
        if (section.model) keys.add(section.model);
    });
    if (!keys.size) report.models.forEach((modelKey) => keys.add(modelKey));
    return Array.from(keys);
}

function ruleSetComputedColumnsForModel(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey) {
    return report.bucketRuleSets.flatMap((ruleSet) => {
        if (modelKeyForRuleSet(context, report, ruleSet) !== modelKey) return [];
        return buildCatRulesColumns(context, report, ruleSet).map((column) => ({
            ...column,
            ruleSetId: ruleSet.id,
            alias: `${safeSqlAlias(ruleSet.name)}_${column.alias}`,
        }));
    });
}

function combinedCategorizationColumnsForModel(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey) {
    const byField: Record<string, { field: ReturnType<typeof targetFieldDef>; whens: string[] }> = {};

    report.bucketRuleSets.forEach((ruleSet) => {
        if (modelKeyForRuleSet(context, report, ruleSet) !== modelKey) return;
        ruleSet.rules.forEach((rule) => {
            if (rule.action !== "set") return;
            const result = ruleResultSql(context, report, rule, modelKey);
            if (!result) return;
            const expression = setRuleMatchExpression(context, report, rule, false, modelKey);
            if (!expression) return;
            const fieldKey = rule.targetField || "category";
            const field = targetFieldDef(context, fieldKey, report);
            byField[fieldKey] = byField[fieldKey] || { field, whens: [] };
            byField[fieldKey].whens.push(`WHEN ${expression} THEN ${result}`);
        });
    });

    return Object.entries(byField).map(([fieldKey, entry]) => {
        // A real column ELSE as text: the THEN branches are NVARCHAR (see resultTextSqlForColumn).
        const elseValue = fieldKey === "category"
            ? sqlLiteral("Everything Else")
            : entry.field.virtual ? entry.field.sql : resultTextSqlForColumn(entry.field);
        const alias = fieldKey === "category" ? "Category" : `${safeSqlAlias(entry.field.sql)}_Result`;
        return {
            fieldKey,
            alias,
            expr: `CASE\n${entry.whens.map((line) => `      ${line}`).join("\n")}\n      ELSE ${elseValue}\n    END`,
        };
    });
}

function ruleMetricColumn(context: ReportLogicContext, report: ReportConfig, section: MetricSection) {
    const ruleSet = report.bucketRuleSets.find((set) => set.id === section.ruleSetId);
    if (!ruleSet || !section.model) return null;
    return ruleSetComputedColumnsForModel(context, report, section.model).find((column) => column.ruleSetId === ruleSet.id) || null;
}

function categorizationOverrideForMetric(context: ReportLogicContext, report: ReportConfig, section: MetricSection, metricColumn?: DataColumn) {
    if (!section.model || !section.breakdownColumn) return null;
    const column = metricColumn || columnsForModel(context, section.model).find((item) => columnMatchesKey(item, section.breakdownColumn));
    if (!column) return null;
    return ruleSetComputedColumnsForModel(context, report, section.model)
        .find((ruleColumn) => {
            const targetColumn = targetFieldDef(context, ruleColumn.fieldKey, report);
            return !targetColumn.virtual && columnsMatch(targetColumn as DataColumn, column);
        }) || null;
}

function metricBreakdownSql(context: ReportLogicContext, report: ReportConfig, section: MetricSection, column: DataColumn) {
    const categorizedColumn = categorizationOverrideForMetric(context, report, section, column);
    const sourceSql = categorizedColumn?.expr || column.sql;
    return {
        breakdown: metricBreakdownExpr(section.groups, sourceSql),
        categorizedColumn,
    };
}

function categorizationFilterClausesForMetric(context: ReportLogicContext, report: ReportConfig, section: MetricSection, column: DataColumn) {
    const categorizedColumn = categorizationOverrideForMetric(context, report, section, column);
    if (!categorizedColumn) return [];
    const ruleSet = report.bucketRuleSets.find((set) => set.id === categorizedColumn.ruleSetId);
    return buildRuleFilterClauses(context, report, ruleSet);
}

function matchingComputedColumn(context: ReportLogicContext, report: ReportConfig, computedColumns: { fieldKey: string; alias: string; expr: string }[], column: DataColumn) {
    return computedColumns.find((ruleColumn) => {
        const targetColumn = targetFieldDef(context, ruleColumn.fieldKey, report);
        return !targetColumn.virtual && columnsMatch(targetColumn as DataColumn, column);
    }) || null;
}

function buildMetricSource(context: ReportLogicContext, plan: PlanConfig, report: ReportConfig, section: MetricSection, column: DataColumn, where: string[]) {
    const model = section.model ? context.dataModels[section.model] : null;
    if (!model || !section.model) {
        return { fromLines: [], computedColumns: [], columnSql: column.sql, usesDerivedSource: false };
    }

    const computedColumns = combinedCategorizationColumnsForModel(context, report, section.model);
    const matchingColumn = matchingComputedColumn(context, report, computedColumns, column);

    return {
        fromLines: [`FROM ${model.table}`, `WHERE ${where.join("\n  AND ")}`],
        computedColumns,
        columnSql: matchingColumn ? matchingColumn.expr : column.sql,
        usesDerivedSource: false,
    };
}

function sourceSqlForColumn(context: ReportLogicContext, report: ReportConfig, computedColumns: { fieldKey: string; alias: string; expr: string }[], column: DataColumn) {
    const matchingColumn = matchingComputedColumn(context, report, computedColumns, column);
    return matchingColumn ? matchingColumn.expr : relatedValueSql(column);
}

interface AggregatedDetailOutput {
    sourceAlias: string;
    label: string;
    expr: string;
    column?: DataColumn;
}

/**
 * The grouped Full Row Detail preview (logic/detailAggregates.ts): aggregated columns per group
 * of every other included column. Mirrors BuildAggregatedDetailSql - the preview names columns
 * by their sheet header where the server uses the technical "detail:..." aliases.
 */
function buildAggregatedDetailSql(
    context: ReportLogicContext,
    report: ReportConfig,
    section: MetricSection,
    model: DataModel,
    layout: SectionExcelLayout,
    outputs: AggregatedDetailOutput[],
    where: string[],
    lines: string[],
) {
    const withTotals = layout.detailTotals?.show === true;
    const included = outputs.filter((output) => isDetailColumnOnSheet(layout, output.sourceAlias));
    if (!included.length) {
        lines.push("-- Include at least one column on the sheet to see its query.");
        return lines.join("\n");
    }
    const groupApply: string[] = [];
    const hoist = (sql: string) => hoistRelatedDimension({ select: sql, groupBy: sql }, groupApply).groupBy;
    const selectItems: string[] = [];
    const groupBy: string[] = [];
    const aggregated: string[] = [];
    included.forEach((output) => {
        const layoutColumn = layout.columns.find((column) => column.sourceAlias === output.sourceAlias);
        const header = layoutColumn?.detail?.header?.trim() || output.label;
        const aggregate = detailAggregateOf(layoutColumn);
        const valueType = detailOutputValueType(context, report, section, output.sourceAlias);
        if (aggregate === "none") {
            if (valueType === "constant") {
                selectItems.push(`${output.expr} AS ${sqlAlias(header)}`);
                return;
            }
            const dimension = hoistRelatedDimension({ select: output.expr, groupBy: output.expr }, groupApply);
            selectItems.push(`${dimension.select} AS ${sqlAlias(header)}`);
            groupBy.push(dimension.groupBy);
            return;
        }
        const dateInput = valueType === "date" && output.column ? dateSqlForColumn({ ...output.column, sql: output.expr }) : output.expr;
        const sql = detailAggregateSql(aggregate, valueType, output.expr, dateInput, hoist);
        selectItems.push(`${sql.value} AS ${sqlAlias(header)}`);
        aggregated.push(`${detailAggregateDef(aggregate).label} of ${output.label}`);
        if (withTotals) selectItems.push(`${sql.total || "NULL"} AS ${sqlAlias(`Total: ${header}`)}`);
    });
    lines.push(`-- Grouped: ${sqlCommentText(aggregated.join(", "))}${groupBy.length ? ", one row per combination of the other columns." : ", one row in total."}`);
    if (withTotals) lines.push("-- \"Total: ...\" columns hold the grand total of each aggregated column (the same on every row).");
    lines.push(`SELECT\n  ${selectItems.join(",\n  ")}`);
    lines.push(`FROM ${relatedFromSql(model)}`);
    lines.push(...relatedGroupApplyLines(groupApply));
    if (!groupBy.length) {
        lines.push(`WHERE ${where.join("\n  AND ")};`);
    } else {
        lines.push(`WHERE ${where.join("\n  AND ")}`);
        lines.push(`GROUP BY ${groupBy.join(",\n  ")}`);
        lines.push(`ORDER BY ${groupBy.join(",\n  ")};`);
    }
    return lines.join("\n");
}

function detailSelectSqlForColumn(context: ReportLogicContext, report: ReportConfig, computedColumns: { fieldKey: string; alias: string; expr: string }[], column: DataColumn) {
    const matchingColumn = matchingComputedColumn(context, report, computedColumns, column);
    // A related column's value list needs a name too (logic/relatedTables.ts).
    if (matchingColumn || isRelatedColumn(column)) return `${sourceSqlForColumn(context, report, computedColumns, column)} AS ${sqlAlias(column.sql || column.label)}`;
    return sourceSqlForColumn(context, report, computedColumns, column);
}

// Detail-layout-aware variant of detailSelectSqlForColumn: always emits an explicit
// alias (the column's effective header from the layout, falling back to its catalog
// label) so a future workbook renderer can bind sheet columns to result columns by
// name instead of by ordinal position. Only used when the section has a "detail" mode
// Excel layout - detailSelectSqlForColumn (unaliased unless rule-computed) still
// covers the SQL preview for a detail metric with no Excel layout enabled.
function detailSelectSqlWithLayoutAlias(context: ReportLogicContext, report: ReportConfig, computedColumns: { fieldKey: string; alias: string; expr: string }[], column: DataColumn, layout: SectionExcelLayout) {
    const source = sourceSqlForColumn(context, report, computedColumns, column);
    const layoutColumn = layout.columns.find((candidate) => candidate.sourceAlias === `detail:${dataColumnSelectionKey(column)}`);
    const header = layoutColumn?.detail?.header?.trim() || column.label;
    return `${source} AS ${sqlAlias(header)}`;
}

// Reorders a detail metric's selected columns to match the user's Excel layout column
// order (see reconcileExcelLayoutColumns), so the SQL preview's column order matches
// the sheet. Columns not yet present in the layout (freshly checked, not reconciled)
// keep their original relative order, appended at the end.
//
// Resolution pool matters here: when the user has explicit selectedColumns, a layout
// alias is only resolved against THOSE columns - never against every visible column on
// the model - so a column removed from selectedColumns (on the Metrics tab, without
// reopening the Excel Layout tab to reconcile) can never be resurrected into the SQL by
// a now-stale layout entry that still references it. The only time the full model
// column pool is used is the SELECT * case (selectedColumns is empty), where the
// layout's own aliases were built from that same full pool in the first place.
function orderDetailColumnsByLayout(selected: DataColumn[], layout: SectionExcelLayout, allDetailColumns: DataColumn[]) {
    const resolvePool = selected.length
        ? new Map(selected.map((column) => [dataColumnSelectionKey(column), column]))
        : new Map(allDetailColumns.map((column) => [dataColumnSelectionKey(column), column]));
    const ordered: DataColumn[] = [];
    const used = new Set<string>();
    layout.columns.forEach((layoutColumn) => {
        if (!layoutColumn.sourceAlias.startsWith("detail:")) return;
        const selectionKey = layoutColumn.sourceAlias.slice("detail:".length);
        const column = resolvePool.get(selectionKey);
        if (column && !used.has(selectionKey)) {
            ordered.push(column);
            used.add(selectionKey);
        }
    });
    selected.forEach((column) => {
        const selectionKey = dataColumnSelectionKey(column);
        if (!used.has(selectionKey)) {
            ordered.push(column);
            used.add(selectionKey);
        }
    });
    return ordered;
}

// NOTE: these CTEs are per-MODEL, shared by every metric on that model, so they cannot
// carry a metric's Co-Plan clients - one CTE would have to satisfy sections with
// different client scopes. They stay scoped to the report plan's own client. This path
// (buildReportSqlPreview) is not wired to the visible SQL preview; if it is ever made
// live, the client scope has to move out of the CTE and into each metric's own WHERE,
// or metrics with Co-Plans will silently lose the extra health plans' rows.
function buildReportCtes(context: ReportLogicContext, plan: PlanConfig, report: ReportConfig, modelKeys: ModelKey[]) {
    return modelKeys.map((modelKey, index) => {
        const model = context.dataModels[modelKey];
        if (!model) return null;
        const computedColumns = ruleSetComputedColumnsForModel(context, report, modelKey);
        const lines = [`${index === 0 ? "WITH" : ","} ${cteNameForModel(modelKey)} AS (`];
        lines.push("  SELECT");
        lines.push("    Source.*");
        computedColumns.forEach((column) => {
            lines.push(`    ,${indentSql(column.expr, 4).trimStart()} AS ${sqlAlias(column.alias)}`);
        });
        lines.push(`  FROM ${model.table} AS Source`);
        lines.push(`  WHERE ${buildBaseWhereClauses(context, plan, report, modelKey).join("\n    AND ")}`);
        lines.push(")");
        return lines.join("\n");
    }).filter(Boolean) as string[];
}

function metricResultSelect(context: ReportLogicContext, report: ReportConfig, section: MetricSection) {
    if (!section.model || !section.breakdownColumn) return null;
    const model = context.dataModels[section.model];
    const column = columnsForModel(context, section.model).find((item) => item.key === section.breakdownColumn);
    if (!model || !column) return null;

    const cteName = cteNameForModel(section.model);
    const metricName = sqlLiteral(section.name || column.label || "Metric");
    const metricFilters = buildMetricFilterClauses(context, report, section, column);
    let resultLabel = column.label;
    let resultSql = metricBreakdownExpr(section.groups, column.sql).select;
    let groupBySql = metricBreakdownExpr(section.groups, column.sql).groupBy;

    if (section.breakdownSource === "ruleSet") {
        const ruleColumn = ruleMetricColumn(context, report, section);
        if (ruleColumn) {
            resultLabel = report.bucketRuleSets.find((set) => set.id === section.ruleSetId)?.name || "Categorization";
            resultSql = sqlAlias(ruleColumn.alias);
            groupBySql = sqlAlias(ruleColumn.alias);
            const ruleSet = report.bucketRuleSets.find((set) => set.id === section.ruleSetId);
            buildRuleFilterClauses(context, report, ruleSet).forEach((clause) => metricFilters.push(clause));
        }
    } else {
        const overrideColumn = categorizationOverrideForMetric(context, report, section);
        if (overrideColumn) {
            resultSql = sqlAlias(overrideColumn.alias);
            groupBySql = sqlAlias(overrideColumn.alias);
        }
    }

    const whereSql = metricFilters.length ? `WHERE ${metricFilters.join("\n    AND ")}` : "";
    // A calculated-field measure replaces COUNT(*); the CTE carries Source.*, so the field's
    // column references resolve the same way they do in the per-metric query.
    const measure = measureAggregateSql(context, report, section) || "COUNT(*)";
    const lines = [
        "SELECT",
        `  ${metricName} AS MetricName,`,
        `  ${sqlLiteral(resultLabel)} AS ResultLabel,`,
        `  CAST(${resultSql} AS NVARCHAR(400)) AS ResultValue,`,
        `  ${measure} AS TotalCount,`,
        section.displayAs === "percentage"
            ? `  ${measure} * 100.0 / NULLIF(SUM(${measure}) OVER (),0) AS Percentage`
            : "  CAST(NULL AS DECIMAL(18,4)) AS Percentage",
        `FROM ${cteName}`,
    ];
    if (whereSql) lines.push(whereSql);
    lines.push(`GROUP BY ${groupBySql}`);
    return lines.join("\n");
}

function buildReportSqlPreview(context: ReportLogicContext, plan: PlanConfig, report: ReportConfig) {
    if (!report.models.length) return "-- No Data Models attached to this report yet.";
    if (!report.sections.length) return "-- No metrics defined yet. Add a metric to generate the report query.";

    const modelKeys = reportModelKeysForSql(report);
    const ctes = buildReportCtes(context, plan, report, modelKeys);
    const metricSelects = report.sections
        .filter((section) => section.displayAs !== "detail" && section.displayAs !== "otp")
        .map((section) => metricResultSelect(context, report, section))
        .filter(Boolean) as string[];

    if (!metricSelects.length) {
        return [
            "-- This report currently has only detail or OTP metrics.",
            "-- Add a count, percentage, or categorization metric to generate the unified report query.",
        ].join("\n");
    }

    return [
        `-- Report query: ${report.label}`,
        ...ctes,
        "SELECT",
        "  ReportResult.MetricName,",
        "  ReportResult.ResultLabel,",
        "  ReportResult.ResultValue,",
        "  ReportResult.TotalCount,",
        "  ReportResult.Percentage",
        "FROM (",
        metricSelects.map((sql) => indentSql(sql, 2)).join("\n  UNION ALL\n"),
        ") AS ReportResult",
        "ORDER BY ReportResult.MetricName, ReportResult.ResultValue;",
    ].join("\n");
}

function buildSqlPreview(context: ReportLogicContext, plan: PlanConfig, report?: ReportConfig, _activeSubTab?: ReportSubTab, useIds = false) {
    if (!report) return "-- Select a report to see its query preview.";
    if (!report.models.length) return "-- No Data Models attached to this report yet.";
    if (!report.sections.length) {
        // Categorization Rule Sets never generate SQL on their own, even if one is defined - a rule
        // set only shows up in the query once some metric explicitly picks it as its breakdown source
        // (see buildSqlForSection). Previously this fell back to previewing a rule set's SQL in
        // isolation whenever the Categorization tab was open, which made it look like categorization
        // was "on" even though no metric had opted into it.
        return "-- No metrics defined yet. Add a metric to generate the report query.";
    }

    const sectionSql = report.sections.map((section) => buildSqlForSection(context, plan, report, section, useIds)).join("\n\n");
    return sectionSql;
}

export {
    reportExclusionClausesForModel,
    tripDataFieldMatchExpr,
    tripDataGroupExpr,
    tripDataClausesForModel,
    periodClauseForModel,
    metricBreakdownExpr,
    extraBreakdownExprs,
    sectionClientScope,
    buildBaseWhereClauses,
    buildWhereClauses,
    metricHeaderLines,
    resolveBreakdownDimension,
    buildSqlForSection,
    buildCombinedRuleSetsSql,
    modelKeyForRuleSet,
    buildCategorizationRuleSetSql,
    buildCategorizationSqlPreview,
    buildCallCenterSql,
    cteNameForModel,
    reportModelKeysForSql,
    ruleSetComputedColumnsForModel,
    combinedCategorizationColumnsForModel,
    ruleMetricColumn,
    categorizationOverrideForMetric,
    metricBreakdownSql,
    categorizationFilterClausesForMetric,
    matchingComputedColumn,
    buildMetricSource,
    sourceSqlForColumn,
    detailSelectSqlForColumn,
    detailSelectSqlWithLayoutAlias,
    orderDetailColumnsByLayout,
    buildReportCtes,
    metricResultSelect,
    buildReportSqlPreview,
    buildSqlPreview,
};
